"use client";

import React, { useState, useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { ComposeHistoryItem } from "@/lib/utils/crm/compose-history";
import { getAllDrafts, deleteDraft, EmailDraft, getDraftKey } from "@/lib/utils/crm/draft-storage";
import { plainTextToHtml } from "@/components/ui/rich-text-editor";
import { EmailRecipient } from "../ClientEmailSelector";
import { ComposeFormValues } from "@/lib/types/compose-modal-types";
import type { Client } from "@/lib/types/database";

export interface ComposeHistoryPanelProps {
  form: UseFormReturn<ComposeFormValues>;
  onClientSelect: (clientId: string) => void;
  onRecipientsChange: (recipients: EmailRecipient[]) => void;
  setCcRecipients: (recipients: EmailRecipient[]) => void;
  setBccRecipients: (recipients: EmailRecipient[]) => void;
  onRestoreFromHistory?: (item: ComposeHistoryItem) => void;
  /** When false, skips loading (e.g. modal closed). When true or undefined, loads on mount/visible. */
  isVisible?: boolean;
}

export function ComposeHistoryPanel({
  form,
  onClientSelect,
  onRecipientsChange,
  setCcRecipients,
  setBccRecipients,
  onRestoreFromHistory,
  isVisible = true,
}: ComposeHistoryPanelProps) {
  const [history, setHistory] = useState<ComposeHistoryItem[]>([]);

  const convertDraftToHistoryItem = useMemo(() => {
    return (draft: EmailDraft): ComposeHistoryItem => {
      const identifier =
        draft.type === "reply" && draft.originalMessageId ? draft.originalMessageId : draft.to;
      const draftKey = getDraftKey(draft.type, identifier);
      const id = draftKey.replace("email_draft_", "");

      const emailList = draft.to
        ? draft.to
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean)
        : [];
      const recipients: ComposeHistoryItem["recipients"] = emailList.map((email, idx) => ({
        id: `${id}-recipient-${idx}`,
        type: "email" as const,
        email,
        name: null,
        clientId:
          draft.selectedClientId && email.includes(draft.selectedClientId)
            ? draft.selectedClientId
            : undefined,
      }));

      return {
        id,
        to: draft.to || "",
        subject: draft.subject || "",
        body: draft.body || "",
        fromAccount: draft.fromAccount || "",
        selectedClientId: draft.selectedClientId || null,
        recipients,
        ccRecipients: [],
        bccRecipients: [],
        variableValues: draft.variableValues || {},
        originalMessageId: draft.originalMessageId,
        timestamp: draft.lastSaved,
      };
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      const drafts = getAllDrafts();
      const historyItems = drafts
        .map(convertDraftToHistoryItem)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 10);
      setHistory(historyItems);
    }
  }, [isVisible, convertDraftToHistoryItem]);

  const handleRestore = (item: ComposeHistoryItem) => {
    const to = item.to || "";
    const cc = item.cc || "";
    const bcc = item.bcc || "";
    const subject = item.subject || "";
    const rawBody = item.body || "";
    const body = rawBody.includes("<") ? rawBody : plainTextToHtml(rawBody);
    const fromAccount = item.fromAccount || "";
    const signature = item.signature || "";

    form.reset({ to, cc, bcc, subject, body, fromAccount, signature });

    if (item.selectedClientId) {
      onClientSelect(item.selectedClientId);
    }

    const restoredRecipients: EmailRecipient[] = (item.recipients || []).map((r) => {
      if (r.clientId) {
        return {
          id: r.id,
          type: "client",
          email: r.email,
          name: r.name || undefined,
          client: { id: r.clientId } as Client,
        } as EmailRecipient;
      }
      return {
        id: r.id,
        type: "email",
        email: r.email,
        name: r.name || undefined,
      } as EmailRecipient;
    });
    onRecipientsChange(restoredRecipients);

    const restoredCcRecipients: EmailRecipient[] = (item.ccRecipients || []).map((r) => {
      if (r.clientId) {
        return {
          id: r.id,
          type: "client",
          email: r.email,
          name: r.name || undefined,
          client: { id: r.clientId } as Client,
        } as EmailRecipient;
      }
      return {
        id: r.id,
        type: "email",
        email: r.email,
        name: r.name || undefined,
      } as EmailRecipient;
    });
    setCcRecipients(restoredCcRecipients);

    const restoredBccRecipients: EmailRecipient[] = (item.bccRecipients || []).map((r) => {
      if (r.clientId) {
        return {
          id: r.id,
          type: "client",
          email: r.email,
          name: r.name || undefined,
          client: { id: r.clientId } as Client,
        } as EmailRecipient;
      }
      return {
        id: r.id,
        type: "email",
        email: r.email,
        name: r.name || undefined,
      } as EmailRecipient;
    });
    setBccRecipients(restoredBccRecipients);

    void form.trigger(["to", "cc", "bcc", "subject", "body", "fromAccount", "signature"]);

    onRestoreFromHistory?.(item);
  };

  const handleDelete = (id: string) => {
    const drafts = getAllDrafts();
    const draftToDelete = drafts.find((draft) => {
      const identifier =
        draft.type === "reply" && draft.originalMessageId ? draft.originalMessageId : draft.to;
      const draftKey = getDraftKey(draft.type, identifier);
      return draftKey.replace("email_draft_", "") === id;
    });

    if (draftToDelete) {
      const identifier =
        draftToDelete.type === "reply" && draftToDelete.originalMessageId
          ? draftToDelete.originalMessageId
          : draftToDelete.to;
      deleteDraft(draftToDelete.type, identifier);

      const updatedDrafts = getAllDrafts();
      const historyItems = updatedDrafts
        .map(convertDraftToHistoryItem)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 10);
      setHistory(historyItems);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {history.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-muted-foreground mb-2">📝</div>
            <div className="text-sm text-muted-foreground">No recent drafts found</div>
          </div>
        </div>
      ) : (
        <>
          <div className="text-xs text-muted-foreground mb-3 px-1">
            {history.length} draft{history.length !== 1 ? "s" : ""} found
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
            {history.map((item) => (
              <div
                key={item.id}
                className="group rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm mb-1 line-clamp-2">
                        {item.subject || "(No subject)"}
                      </div>
                      <div className="text-xs text-muted-foreground mb-2 line-clamp-1">
                        To: {item.to || "No recipients"}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {new Date(item.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100 transition-opacity">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-xs"
                        onClick={() => handleRestore(item)}
                      >
                        Restore
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-xs text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(item.id)}
                        aria-label="Delete draft"
                      >
                        <span className="sr-only">Delete</span>✕
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
